import { EnumButtonName } from '@calm/evexia-analytics';
import { ChevronRight } from '@mui/icons-material';
import commonMessages from '@rhg/lang/messages/common';
import {
    ListItem,
    ListItemAvatar,
    ListItemText,
    ListItemButton,
    SxProps,
    Theme,
} from '@rhg/libs/material';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ContributorAvatar } from '../avatar';
import { Contributor, ContributorType } from '../types';

interface ContributorListButtonProps {
    contributor: Contributor;
    contributorType: ContributorType;
    clickable: true;
    onClick: (contributor: Contributor) => void;
    trackButton: EnumButtonName;
    subtitleOverride?: string;
    roleLabel?: string;
    sx?: SxProps<Theme>;
}
interface ContributorListItemProps {
    contributor: Contributor;
    contributorType: ContributorType;
    subtitleOverride?: string;
    roleLabel?: string;
    clickable?: false;
    onClick?: undefined;
    trackButton?: undefined;
    sx?: SxProps<Theme>;
}
export function ContributorListItem({
    contributor,
    contributorType,
    subtitleOverride,
    roleLabel,
    clickable,
    onClick,
    trackButton,
    sx,
}: ContributorListItemProps | ContributorListButtonProps) {
    const { formatMessage } = useIntl();

    const title = useMemo(
        () =>
            contributorType === ContributorType.Author
                ? formatMessage(commonMessages['common.clinical_author'])
                : formatMessage(commonMessages['common.narrator']),
        [contributorType, formatMessage]
    );

    const contents = useMemo(
        () => (
            <>
                <ListItemAvatar>
                    <ContributorAvatar contributor={contributor} size="small" />
                </ListItemAvatar>
                <ListItemText
                    primary={contributor?.name}
                    secondary={subtitleOverride ?? roleLabel ?? title}
                />
                {clickable && <ChevronRight />}
            </>
        ),
        [clickable, contributor, subtitleOverride, roleLabel, title]
    );

    const handleClick = useCallback(
        () => onClick?.(contributor),
        [contributor, onClick]
    );

    // Render ListItemButton/ListItem directly. Previously the container was a
    // component built with useCallback and rendered as `<Container>` — because
    // its identity changed whenever a caller passed a fresh `onClick`, React
    // treated it as a new component type and REMOUNTED the whole subtree
    // (including the narrator avatar) on every parent re-render, which flashed
    // the avatar in Safari on play/pause. EVE-13080.
    return clickable ? (
        <ListItemButton sx={sx} onClick={handleClick} trackButton={trackButton}>
            {contents}
        </ListItemButton>
    ) : (
        <ListItem sx={sx}>{contents}</ListItem>
    );
}
